import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api, { pdvApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  MapPin,
  Search,
  CheckCircle,
  Clock,
  FileText,
  Store,
  Upload,
  File,
  Trash2,
  Download,
  Loader2,
  Eye,
  FolderOpen
} from 'lucide-react';

export default function GroundInspectionsPage() {
  const { user } = useAuth();
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPdv, setSelectedPdv] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documents, setDocuments] = useState({});
  const [uploading, setUploading] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const pdvRes = await pdvApi.getAll();
      setPdvs(pdvRes.data);
      
      // Carica i documenti per ogni PDV
      await loadAllDocuments(pdvRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadAllDocuments = async (pdvList) => {
    const docsMap = {};
    for (const pdv of pdvList) {
      try {
        const res = await api.get(`/ground-inspections/documents/${pdv.id}`);
        docsMap[pdv.id] = res.data || [];
      } catch (e) {
        docsMap[pdv.id] = [];
      }
    }
    setDocuments(docsMap);
  };

  const loadPdvDocuments = async (pdvId) => {
    setLoadingDocs(true);
    try {
      const res = await api.get(`/ground-inspections/documents/${pdvId}`);
      setDocuments(prev => ({ ...prev, [pdvId]: res.data || [] }));
    } catch (e) {
      setDocuments(prev => ({ ...prev, [pdvId]: [] }));
    } finally {
      setLoadingDocs(false);
    }
  };

  const filteredPdvs = pdvs.filter(pdv =>
    pdv.name?.toLowerCase().includes(search.toLowerCase()) ||
    pdv.city?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenUpload = (pdv) => {
    setSelectedPdv(pdv);
    loadPdvDocuments(pdv.id);
    setDialogOpen(true);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Verifica che siano tutti PDF
    const invalidFiles = files.filter(f => f.type !== 'application/pdf');
    if (invalidFiles.length > 0) {
      toast.error('Sono accettati solo file PDF');
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('pdv_id', selectedPdv.id);
        formData.append('uploaded_by', user?.full_name || user?.email);

        await api.post('/ground-inspections/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success(`${files.length} documento/i caricato/i con successo`);
      await loadPdvDocuments(selectedPdv.id);
    } catch (error) {
      toast.error('Errore nel caricamento: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;
    
    try {
      await api.delete(`/ground-inspections/documents/${docId}`);
      toast.success('Documento eliminato');
      await loadPdvDocuments(selectedPdv.id);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await api.get(`/ground-inspections/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Errore nel download');
    }
  };

  const getDocCount = (pdvId) => {
    return documents[pdvId]?.length || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="ground-inspections-page"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="w-6 h-6" />
            Verifiche di Terra
          </h1>
          <p className="text-muted-foreground">Carica documenti PDF per le verifiche delle Unità Locali</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">UL Assegnate</p>
                <p className="text-2xl font-bold">{pdvs.length}</p>
              </div>
              <Store className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Con Documenti</p>
                <p className="text-2xl font-bold text-green-600">
                  {pdvs.filter(p => getDocCount(p.id) > 0).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Senza Documenti</p>
                <p className="text-2xl font-bold text-amber-600">
                  {pdvs.filter(p => getDocCount(p.id) === 0).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Cerca Unità Locale..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* PDV List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPdvs.map((pdv) => {
          const docCount = getDocCount(pdv.id);
          
          return (
            <Card key={pdv.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Store className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{pdv.name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {pdv.city}
                      </p>
                    </div>
                  </div>
                  <Badge className={docCount > 0 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                    {docCount} doc
                  </Badge>
                </div>
                
                <p className="text-sm text-muted-foreground mb-3">{pdv.address}</p>
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <FileText className="w-4 h-4" />
                  <span>{docCount} documento/i caricato/i</span>
                </div>
                
                <Button className="w-full" onClick={() => handleOpenUpload(pdv)}>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Gestisci Documenti
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredPdvs.length === 0 && (
        <div className="text-center py-12">
          <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Nessuna Unità Locale trovata</p>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documenti - {selectedPdv?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* PDV Info */}
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <Store className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium">{selectedPdv?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedPdv?.address}, {selectedPdv?.city}</p>
                </div>
              </div>
            </div>

            {/* Upload Area */}
            <div>
              <Label className="text-base font-semibold mb-2 block">Carica Nuovi Documenti</Label>
              <div 
                className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mb-2" />
                    <p>Caricamento in corso...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <p className="font-medium">Clicca per caricare PDF</p>
                    <p className="text-sm text-muted-foreground">o trascina qui i file</p>
                  </div>
                )}
              </div>
            </div>

            {/* Document List */}
            <div>
              <Label className="text-base font-semibold mb-2 block">
                Documenti Caricati ({documents[selectedPdv?.id]?.length || 0})
              </Label>
              
              {loadingDocs ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : documents[selectedPdv?.id]?.length > 0 ? (
                <div className="space-y-2">
                  {documents[selectedPdv?.id].map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-3">
                        <File className="w-8 h-8 text-red-500" />
                        <div>
                          <p className="font-medium text-sm">{doc.filename}</p>
                          <p className="text-xs text-muted-foreground">
                            Caricato il {new Date(doc.uploaded_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })} da {doc.uploaded_by}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDownloadDocument(doc)}
                          title="Scarica"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="text-destructive"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nessun documento caricato</p>
                  <p className="text-sm">Clicca sopra per caricare il primo PDF</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
